<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  unit: { type: Object, required: true },
  canManage: { type: Boolean, default: false },
});

const emit = defineEmits(['addChild', 'edit', 'remove', 'members', 'channels']);

const { t } = useI18n();

const typeLabel = computed(() =>
  t(`ORG_ACCESS.UNIT_TYPE.${props.unit.unit_type.toUpperCase()}`)
);
const memberCount = computed(() => props.unit.members?.length || 0);
const channelCount = computed(() => props.unit.inbox_ids?.length || 0);
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      class="flex flex-wrap items-center gap-3 px-4 py-3 border rounded-lg bg-n-solid-2 border-n-weak"
    >
      <div class="flex flex-col flex-1 min-w-40">
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ unit.name }}
          </span>
          <span
            class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11"
          >
            {{ typeLabel }}
          </span>
        </div>
        <span class="text-xs text-n-slate-11">
          {{ t('ORG_ACCESS.STRUCTURE.MEMBER_COUNT', { count: memberCount }) }}
          ·
          {{ t('ORG_ACCESS.STRUCTURE.CHANNEL_COUNT', { count: channelCount }) }}
        </span>
      </div>
      <div v-if="canManage" class="flex flex-wrap items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          :label="t('ORG_ACCESS.STRUCTURE.MEMBERS')"
          @click="emit('members', unit)"
        />
        <Button
          size="sm"
          variant="ghost"
          :label="t('ORG_ACCESS.STRUCTURE.CHANNELS')"
          @click="emit('channels', unit)"
        />
        <Button
          size="sm"
          variant="ghost"
          :label="t('ORG_ACCESS.STRUCTURE.ADD_CHILD')"
          @click="emit('addChild', unit)"
        />
        <Button
          size="sm"
          variant="ghost"
          :label="t('ORG_ACCESS.STRUCTURE.EDIT')"
          @click="emit('edit', unit)"
        />
        <Button
          size="sm"
          variant="ghost"
          color="ruby"
          :label="t('ORG_ACCESS.STRUCTURE.DELETE')"
          @click="emit('remove', unit)"
        />
      </div>
    </div>

    <div
      v-if="unit.children && unit.children.length"
      class="flex flex-col gap-2 ltr:pl-6 rtl:pr-6 ltr:border-l rtl:border-r border-n-weak"
    >
      <OrgUnitNode
        v-for="child in unit.children"
        :key="child.id"
        :unit="child"
        :can-manage="canManage"
        @add-child="emit('addChild', $event)"
        @edit="emit('edit', $event)"
        @remove="emit('remove', $event)"
        @members="emit('members', $event)"
        @channels="emit('channels', $event)"
      />
    </div>
  </div>
</template>
